import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiGrid, FiBox, FiShoppingBag, FiUsers, FiSettings,
  FiMenu, FiX, FiLogOut, FiFacebook, FiHelpCircle, FiLayers,
  FiTrendingUp, FiExternalLink, FiBell, FiStar
} from 'react-icons/fi';
import { useAuthStore } from '../../store';
import { logoutUser } from '../../firebase/auth';
import AdminDashboard from './AdminDashboard';
import AdminProducts from './AdminProducts';
import AdminCollections from './AdminCollections';
import AdminOrders from './AdminOrders';
import AdminUsers from './AdminUsers';
import AdminMeta from './AdminMeta';
import AdminFAQs from './AdminFAQs';
import AdminSettings from './AdminSettings';
import AdminOrderView from './AdminOrderView';
import AdminReviews from './AdminReviews';
import toast from 'react-hot-toast';

const AdminLayout = () => {
  const { user, userRole, clearUser } = useAuthStore();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false); // desktop: collapsed/expanded
  const [mobileOpen, setMobileOpen] = useState(false); // mobile: drawer open/closed

  if (!user || userRole !== 'admin') return <Navigate to="/" />;

  const handleLogout = async () => {
    await logoutUser();
    clearUser();
    toast.success('Logged out');
  };

  const navGroups = [
    {
      label: 'Main',
      items: [
        { to: '/admin', label: 'Dashboard', icon: FiGrid, exact: true },
      ]
    },
    {
      label: 'Catalog',
      items: [
        { to: '/admin/products', label: 'Products', icon: FiBox },
        { to: '/admin/collections', label: 'Collections', icon: FiLayers },
        { to: '/admin/reviews', label: 'Reviews', icon: FiStar },
      ]
    },
    {
      label: 'Sales',
      items: [
        { to: '/admin/orders', label: 'Orders', icon: FiShoppingBag },
        { to: '/admin/users', label: 'Users & Roles', icon: FiUsers },
      ]
    },
    {
      label: 'Marketing',
      items: [
        { to: '/admin/meta', label: 'Meta / Facebook', icon: FiFacebook },
        { to: '/admin/faqs', label: 'FAQs', icon: FiHelpCircle },
      ]
    },
    {
      label: 'System',
      items: [
        { to: '/admin/settings', label: 'Settings', icon: FiSettings },
      ]
    }
  ];

  const isActive = (to, exact) => exact ? location.pathname === to : location.pathname.startsWith(to);
  const currentPage = navGroups.flatMap(g => g.items).find(item => isActive(item.to, item.exact))?.label || 'Admin';

  // Close the mobile drawer whenever the route changes (e.g. after tapping a link)
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* Logo */}
      <div className={`border-b border-white/8 flex items-center justify-between ${collapsed && !isMobile ? 'px-4 py-5' : 'px-6 py-5'}`}>
        {collapsed && !isMobile ? (
          <img src="/logo-icon.png" alt="SitSmart" className="h-8 w-8 object-contain bg-white p-1 rounded-md mx-auto" />
        ) : (
          <div>
            <img src="/logo.png" alt="SitSmart" className="h-10 w-auto object-contain bg-white px-2 py-1 rounded-md" />
            <p className="text-white/30 text-[10px] uppercase tracking-[0.25em] mt-1.5">Admin Panel</p>
          </div>
        )}
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="text-white/50 hover:text-white p-1">
            <FiX className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
        {navGroups.map(group => (
          <div key={group.label} className="mb-4">
            {(!collapsed || isMobile) && (
              <p className="text-white/25 text-[9px] font-black uppercase tracking-[0.35em] px-5 mb-2">{group.label}</p>
            )}
            <div className="space-y-0.5 px-3">
              {group.items.map(({ to, label, icon: Icon, exact }) => {
                const active = isActive(to, exact);
                return (
                  <Link key={to} to={to} title={collapsed && !isMobile ? label : undefined}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative ${
                      active
                        ? 'bg-gradient-to-r from-[#e63946] to-[#c1121f] text-white shadow-lg shadow-[#e63946]/20'
                        : 'text-white/50 hover:bg-white/8 hover:text-white'
                    }`}
                  >
                    {active && (!collapsed || isMobile) && (
                      <motion.div layoutId={isMobile ? 'activeNavMobile' : 'activeNav'} className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#e63946] to-[#c1121f]" style={{ zIndex: -1 }} />
                    )}
                    <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-white' : ''}`} />
                    {(!collapsed || isMobile) && <span className="text-sm font-semibold truncate">{label}</span>}
                    {collapsed && !isMobile && (
                      <div className="absolute left-full ml-3 px-2.5 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity hidden lg:block">
                        {label}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User */}
      <div className={`border-t border-white/8 p-3 ${collapsed && !isMobile ? 'flex justify-center' : ''}`}>
        {collapsed && !isMobile ? (
          <button onClick={handleLogout} className="w-10 h-10 bg-[#e63946]/20 rounded-full flex items-center justify-center text-[#e63946] hover:bg-[#e63946] hover:text-white transition-all" title="Logout">
            <FiLogOut className="w-4 h-4" />
          </button>
        ) : (
          <div className="flex items-center gap-3 p-2">
            <div className="w-9 h-9 bg-gradient-to-br from-[#e63946] to-[#c1121f] rounded-full flex items-center justify-center font-black text-white text-sm flex-shrink-0 shadow-md">
              {user.displayName?.[0]?.toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-bold truncate">{user.displayName || 'Admin'}</p>
              <p className="text-white/30 text-[10px] truncate">{user.email}</p>
            </div>
            <button onClick={handleLogout} title="Logout"
              className="w-7 h-7 text-white/30 hover:text-red-400 hover:bg-white/5 rounded-lg flex items-center justify-center transition-all flex-shrink-0">
              <FiLogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#f0f2f8' }}>
      {/* Desktop Sidebar — hidden below lg, normal flow above lg */}
      <motion.aside
        animate={{ width: collapsed ? 70 : 240 }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="hidden lg:flex bg-[#0b1f42] flex-col z-30 flex-shrink-0 overflow-hidden shadow-2xl"
      >
        <SidebarContent />
      </motion.aside>

      {/* Mobile Sidebar — off-canvas drawer with backdrop, only below lg */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/60 z-40"
            />
            <motion.aside
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="lg:hidden fixed inset-y-0 left-0 w-[280px] max-w-[85vw] bg-[#0b1f42] flex flex-col z-50 shadow-2xl"
            >
              <SidebarContent isMobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-3.5 flex items-center justify-between flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            {/* Mobile: opens drawer. Desktop: collapses/expands sidebar */}
            <button
              onClick={() => {
                if (window.innerWidth < 1024) setMobileOpen(true);
                else setCollapsed(!collapsed);
              }}
              className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-900 flex-shrink-0"
            >
              <FiMenu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 text-sm text-gray-500 min-w-0">
              <span className="hidden sm:block">Admin</span>
              <span className="hidden sm:block text-gray-300">/</span>
              <span className="font-semibold text-gray-900 truncate">{currentPage}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Link to="/" target="_blank"
              className="hidden md:flex items-center gap-1.5 text-sm text-[#1a3a6e] hover:text-[#e63946] font-semibold transition-colors">
              <FiExternalLink className="w-4 h-4" /> View Store
            </Link>
            <div className="w-8 h-8 bg-gradient-to-br from-[#1a3a6e] to-[#e63946] rounded-full flex items-center justify-center text-white font-black text-xs flex-shrink-0">
              {user.displayName?.[0]?.toUpperCase() || 'A'}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
          <Routes>
            <Route path="/" element={<AdminDashboard />} />
            <Route path="/products/*" element={<AdminProducts />} />
            <Route path="/collections" element={<AdminCollections />} />
            <Route path="/orders" element={<AdminOrders />} />
            <Route path="/orders/:id" element={<AdminOrderView />} />
            <Route path="/reviews" element={<AdminReviews />} />
            <Route path="/users" element={<AdminUsers />} />
            <Route path="/meta" element={<AdminMeta />} />
            <Route path="/faqs" element={<AdminFAQs />} />
            <Route path="/settings" element={<AdminSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
